import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../api/client';

const PluginContext = createContext();

export const PluginProvider = ({ children }) => {
    const [activePlugins, setActivePlugins] = useState({});
    const [pluginsLoaded, setPluginsLoaded] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const fetchActivePlugins = async () => {
            try {
                const response = await api.get('/api/v1/plugins/active', {
                    params: { _: Date.now() },
                    headers: {
                        'Cache-Control': 'no-cache',
                        Pragma: 'no-cache'
                    }
                });

                const list = Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response?.data?.data)
                        ? response.data.data
                        : [];

                // Convert array to a key-value map for easy O(1) lookups
                const pluginMap = {};
                list.forEach(plugin => {
                    pluginMap[plugin.id] = plugin;
                });
                if (isMounted) {
                    setActivePlugins(pluginMap);
                }
            } catch (error) {
                console.error("Failed to fetch active plugins:", error);
            } finally {
                if (isMounted) {
                    setPluginsLoaded(true);
                }
            }
        };

        fetchActivePlugins();
        const onVisibilityChange = () => {
            if (!document.hidden) {
                fetchActivePlugins();
            }
        };
        const onFocus = () => fetchActivePlugins();
        const intervalId = window.setInterval(fetchActivePlugins, 30000);

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('focus', onFocus);

        return () => {
            isMounted = false;
            window.clearInterval(intervalId);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('focus', onFocus);
        };
    }, []);

    // Helper to easily check if a plugin is active
    const isPluginActive = (pluginId) => {
        return !!activePlugins[pluginId];
    };

    // Helper to get a plugin's explicit JSON configuration
    const getPluginConfig = (pluginId) => {
        return activePlugins[pluginId]?.configuration || {};
    };

    return (
        <PluginContext.Provider value={{ activePlugins, pluginsLoaded, isPluginActive, getPluginConfig }}>
            {children}
        </PluginContext.Provider>
    );
};

export const usePlugins = () => useContext(PluginContext);
